/**
 * Shifts List Page — shift grid with time range + employee count badge.
 */

import * as React from 'react';
import { useList, useDelete } from '@refinedev/core';
import { useNavigate } from 'react-router';
import {
  Button,
  Input,
  Card,
  CardContent,
  Badge,
  EmptyState,
} from '@yesbos/ui';
import { Plus, Search, Clock } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────

interface Shift {
  id: string;
  name: string;
  start_time?: string;
  end_time?: string;
  type?: string;
  days?: string;
  description?: string;
}

// ── Shift Card ────────────────────────────────────────────────────────────────

function ShiftCard({ shift, onClick, onEdit, onDelete }: { shift: Shift; onClick: () => void; onEdit: () => void; onDelete: () => void }) {
  const timeRange =
    shift.start_time && shift.end_time
      ? `${shift.start_time} – ${shift.end_time}`
      : shift.start_time ?? shift.end_time ?? null;

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <p className="font-medium text-sm">{shift.name}</p>
          {shift.type && (
            <Badge variant="outline" className="text-xs capitalize shrink-0">
              {shift.type}
            </Badge>
          )}
        </div>
        {timeRange && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            {timeRange}
          </p>
        )}
        {shift.days && (
          <p className="text-xs text-muted-foreground mt-1">{shift.days}</p>
        )}
        {shift.description && (
          <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
            {shift.description}
          </p>
        )}
        <div className="flex items-center justify-end gap-1 mt-3">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="text-xs text-muted-foreground hover:text-foreground px-1"
          >Edit</button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="text-xs text-destructive hover:underline px-1"
          >Delete</button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function ShiftsListPage() {
  const navigate = useNavigate();
  const { mutate: deleteShift } = useDelete();
  const [search, setSearch] = React.useState('');

  const filters = React.useMemo(() => {
    if (!search.trim()) return [];
    return [{ field: 'name', operator: 'contains', value: search.trim() }];
  }, [search]);

  const { data, isLoading } = useList<Shift>({
    resource: 'shifts',
    filters,
    pagination: { pageSize: 50 },
    sorters: [{ field: 'name', order: 'asc' }],
  });

  const shifts = (data?.data ?? []) as Shift[];

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Shifts</h1>
          {!isLoading && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {data?.total ?? shifts.length} shift{(data?.total ?? shifts.length) !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <Button onClick={() => navigate('/shifts/create')}>
          <Plus className="h-4 w-4 mr-2" /> Add Shift
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search shifts…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-24 rounded-lg border bg-muted animate-pulse" />
          ))}
        </div>
      ) : shifts.length === 0 ? (
        <EmptyState
          icon={<Clock className="h-8 w-8" />}
          title="No Shifts Found"
          description={search ? 'Try a different search.' : 'Create your first shift to get started.'}
          action={!search ? { label: 'Add Shift', onClick: () => navigate('/shifts/create') } : undefined}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {shifts.map((shift) => (
            <ShiftCard
              key={shift.id}
              shift={shift}
              onClick={() => navigate(`/shifts/${shift.id}`)}
              onEdit={() => navigate(`/shifts/${shift.id}/edit`)}
              onDelete={() => {
                if (!window.confirm(`Delete shift "${shift.name}"? This cannot be undone.`)) return;
                deleteShift({ resource: 'shifts', id: shift.id });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default ShiftsListPage;
